/**
 * Sign Document Page - Consumer Portal
 *
 * Standalone full-page signing component for consumers to sign documents
 * sent via DocuSeal. This page is PUBLIC (no auth required) since
 * borrowers may not have a portal account.
 *
 * Route: /sign/:submitterId
 * Extracts submitterId from URL, fetches the DocuSeal embed URL,
 * and renders a full-page iframe for signing.
 *
 * Issue #56 - E-Sign Bridge
 */

import CheckIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/ErrorOutlined';
import {
  Box,
  GlassButton,
  GlassCard,
  iconSizes,
  ShimmerLoader,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEsignEmbedUrl } from '../../lib/trpc/hooks';

type SigningState = 'loading' | 'signing' | 'success' | 'error' | 'expired';

export default function SignDocumentPage() {
  const { t } = useI18n();
  const { submitterId: submitterIdParam } = useParams<{ submitterId: string }>();
  const navigate = useNavigate();

  const [signingState, setSigningState] = useState<SigningState>('loading');

  const submitterId = submitterIdParam ? Number.parseInt(submitterIdParam, 10) : null;
  const validSubmitterId = submitterId && !Number.isNaN(submitterId) ? submitterId : null;

  const { data: embedData, isLoading, error } = useEsignEmbedUrl(validSubmitterId);

  // Update signing state based on query result
  useEffect(() => {
    if (isLoading) {
      setSigningState('loading');
    } else if (error) {
      // Check if this is an expired link error
      const errorMessage = error instanceof Error ? error.message : '';
      if (errorMessage.includes('expired') || errorMessage.includes('not found')) {
        setSigningState('expired');
      } else {
        setSigningState('error');
      }
    } else if (embedData?.url) {
      setSigningState('signing');
    }
  }, [isLoading, error, embedData]);

  // Listen for DocuSeal postMessage completion events
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'docuseal:completed') {
        setSigningState('success');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Redirect to DocuSeal URL directly (avoids X-Frame-Options issues with iframe)
  useEffect(() => {
    if (embedData?.url && signingState === 'signing') {
      window.location.href = embedData.url;
    }
  }, [embedData?.url, signingState]);

  const handleBackToDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  // Error state: invalid submitter ID in URL
  if (!validSubmitterId) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <GlassCard sx={{ maxWidth: 480, p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: iconSizes['4xl'], color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {t('esign.consumer.documentExpired')}
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {t('esign.consumer.documentExpiredHelp')}
          </Typography>
          <GlassButton variant="gradient" onClick={handleBackToDashboard}>
            {t('esign.consumer.backToDashboard')}
          </GlassButton>
        </GlassCard>
      </Box>
    );
  }

  // Loading state
  if (signingState === 'loading') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          gap: 3,
        }}
      >
        <ShimmerLoader variant="circular" width={64} height={64} />
        <Typography variant="h6" color="textSecondary">
          {t('esign.consumer.loadingDocument')}
        </Typography>
      </Box>
    );
  }

  // Success state
  if (signingState === 'success') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <GlassCard sx={{ maxWidth: 480, p: 4, textAlign: 'center' }}>
          <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            {t('esign.consumer.signedSuccess')}
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {t('esign.consumer.signedSuccessHelp')}
          </Typography>
          <GlassButton variant="gradient" onClick={handleBackToDashboard}>
            {t('esign.consumer.backToDashboard')}
          </GlassButton>
        </GlassCard>
      </Box>
    );
  }

  // Expired state
  if (signingState === 'expired') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <GlassCard sx={{ maxWidth: 480, p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: iconSizes['4xl'], color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {t('esign.consumer.documentExpired')}
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {t('esign.consumer.documentExpiredHelp')}
          </Typography>
          <GlassButton variant="gradient" onClick={handleBackToDashboard}>
            {t('esign.consumer.backToDashboard')}
          </GlassButton>
        </GlassCard>
      </Box>
    );
  }

  // Error state
  if (signingState === 'error') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <GlassCard sx={{ maxWidth: 480, p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: iconSizes['4xl'], color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {t('esign.signing.failed')}
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {t('esign.signing.failedHelp')}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
            <GlassButton variant="outlined" onClick={() => window.location.reload()}>
              {t('common.retry', { defaultValue: 'Try Again' })}
            </GlassButton>
            <GlassButton variant="gradient" onClick={handleBackToDashboard}>
              {t('esign.consumer.backToDashboard')}
            </GlassButton>
          </Stack>
        </GlassCard>
      </Box>
    );
  }

  // Show loading while resolving the embed URL
  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        bgcolor: 'background.default',
      }}
    >
      <ShimmerLoader variant="circular" width={64} height={64} />
      <Typography variant="h6" color="textSecondary">
        {t('esign.consumer.loadingDocument')}
      </Typography>
    </Box>
  );
}
